import { validateNameQuestion } from 'components/ZentakeForm/Questions/NameQuestion'
import { validatePhoneQuestion } from 'components/ZentakeForm/Questions/PhoneNumberQuestion'
import { validateEmailQuestion } from 'components/ZentakeForm/Questions/SingleLineQuestion'
import every from 'lodash/every'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'
import flatMap from 'lodash/flatMap'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import reject from 'lodash/reject'
import some from 'lodash/some'
import validateTableQuestion from 'utils/questionValidators/validateTableQuestion'

import { visibleByConditionalRulesHelper } from './formHelpers'
import { getDisabledFlagsByMinAndMax } from './getDisabledFlagsByMinAndMax'
import {
  addToArrayIfEvery,
  getMatrixCoordinateId,
  hasText,
  removeArrayIndex,
  removeFromArrayIf,
} from './helpers'

function getAllQuestionsFromForm(form) {
  const questions = []

  if (!form?.pages) return questions

  form.pages.forEach((page) => {
    page.sections.forEach((section) => {
      if (section.rows) {
        section.rows.forEach((row) => {
          row.questions.forEach((question) => {
            questions.push(question)
          })
        })
      } else if (section.questions) {
        section.questions.forEach((question) => {
          questions.push(question)
        })
      }
    })
  })

  return questions
}

function applyFileAnswerToResponse(response, question, files) {
  const result = {
    ...response,
  }

  const answerIndex = result.answers.findIndex(
    (a) => a.question === question.id
  )

  if (answerIndex >= 0) {
    result.answers[answerIndex] = {
      id: null,
      question: question.id,
      answer_files: files,
      isFile: true,
    }
  } else {
    response.answers.push({
      id: null,
      question: question.id,
      answer_files: files,
      isFile: true,
    })
  }

  return result
}

function applyMarkerAnswerToResponse(response, question, markup) {
  const result = {
    ...response,
  }

  const answerIndex = result.answers.findIndex(
    (a) => a.question === question.id
  )

  if (answerIndex >= 0) {
    result.answers[answerIndex].answer_raw = markup
  } else {
    response.answers.push({
      id: null,
      question: question.id,
      answer_raw: markup,
    })
  }

  return result
}

function applySingleLineAnswerToResponse(response, question, text) {
  const result = {
    ...response,
  }

  const answerIndex = result.answers.findIndex(
    (a) => a.question === question.id
  )

  if (answerIndex >= 0) {
    result.answers[answerIndex].answer_raw = text
    result.answers[answerIndex].answer_text = text
  } else {
    response.answers.push({
      id: null,
      question: question.id,
      answer_raw: text,
      answer_text: text,
    })
  }

  return result
}

function applyCreditCardAnswerToResponse(response, question, isFilled) {
  const textValue = isFilled ? '**** **** **** ****' : ''
  return applySingleLineAnswerToResponse(response, question, textValue)
}

function applyNumberAnswerToResponse(response, question, number) {
  const result = {
    ...response,
  }

  const answerIndex = result.answers.findIndex(
    (a) => a.question === question.id
  )

  if (answerIndex >= 0) {
    result.answers[answerIndex].answer_raw = !isNil(number) ? `${number}` : null
  } else {
    response.answers.push({
      id: null,
      question: question.id,
      answer_raw: !isNil(number) ? `${number}` : null,
    })
  }

  return result
}

function applyCheckboxListAnswerToResponse(
  response,
  question,
  { value, choiceId, textDetails }
) {
  const result = {
    ...response,
  }

  const answerIndex = result.answers.findIndex(
    (a) => a.question === question.id
  )

  if (answerIndex >= 0) {
    const elements = result.answers[answerIndex].answer_raw.split(',')

    const newElements = value
      ? addToArrayIfEvery(
          elements,
          choiceId.toString(),
          (x) => x !== choiceId.toString()
        )
      : removeFromArrayIf(elements, (x) => x === choiceId.toString())

    result.answers[answerIndex].answer_raw = newElements
      .filter((x) => !!x)
      .join(',')

    const text_details = result.answers[answerIndex]?.text_details ?? {}
    if (value === true && hasText(textDetails)) {
      result.answers[answerIndex].text_details = {
        ...result.answers[answerIndex].text_details,
        [choiceId]: textDetails,
      }
    } else if (
      value === false &&
      !hasText(textDetails) &&
      !isNil(text_details?.[choiceId])
    ) {
      const { [choiceId]: $, ...rest } = text_details
      result.answers[answerIndex].text_details = rest ?? {}
    }
  } else if (value) {
    response.answers.push({
      id: null,
      question: question.id,
      answer_raw: choiceId.toString(),
    })
  }

  return result
}

function applyRadioListAnswerToResponse(
  response,
  question,
  { choiceId, textDetails }
) {
  const result = {
    ...response,
  }

  const answerIndex = result.answers.findIndex(
    (a) => a.question === question.id
  )

  if (answerIndex >= 0) {
    result.answers[answerIndex].answer_raw = choiceId.toString()
    result.answers[answerIndex].text_details = textDetails
  } else {
    response.answers.push({
      id: null,
      question: question.id,
      answer_raw: choiceId.toString(),
      text_details: textDetails,
    })
  }

  return result
}

function noOpApply(response) {
  return response
}

function applyDateOnlyAnswerToResponse(response, question, date) {
  const result = {
    ...response,
  }

  const answerIndex = result.answers.findIndex(
    (a) => a.question === question.id
  )

  if (answerIndex >= 0) {
    result.answers[answerIndex].answer_raw = date
    result.answers[answerIndex].answer_date = date
  } else {
    response.answers.push({
      id: null,
      question: question.id,
      answer_raw: date,
      answer_date: date,
    })
  }

  return result
}

function applyDateTimeAnswerToResponse(response, question, dateTime) {
  const result = {
    ...response,
  }

  const answerIndex = result.answers.findIndex(
    (a) => a.question === question.id
  )

  if (answerIndex >= 0) {
    result.answers[answerIndex].answer_raw = dateTime
    result.answers[answerIndex].answer_datetime = dateTime
  } else {
    response.answers.push({
      id: null,
      question: question.id,
      answer_raw: dateTime,
      answer_datetime: dateTime,
    })
  }

  return result
}

function applyTimeOnlyAnswerToResponse(response, question, time) {
  const result = {
    ...response,
  }

  const answerIndex = result.answers.findIndex(
    (a) => a.question === question.id
  )

  if (answerIndex >= 0) {
    result.answers[answerIndex].answer_raw = time
    result.answers[answerIndex].answer_time = time
  } else {
    response.answers.push({
      id: null,
      question: question.id,
      answer_raw: time,
      answer_time: time,
    })
  }

  return result
}

function applySignatureAnswerToResponse(
  response,
  question,
  { id, image, text, ip_address, created_at, updated_at, untrimmedImage }
) {
  const result = {
    ...response,
  }

  const isEmptyAnswer = isNil(image) && isNil(text)

  const answerIndex = result.answers.findIndex(
    (a) => a.question === question.id
  )

  if (answerIndex >= 0) {
    const nextAnswer = {
      id,
      image,
      untrimmedImage,
      text,
      ip_address,
      updated_at,
      created_at,
    }
    result.answers[answerIndex] = {
      ...(result.answers?.[answerIndex] ?? {}),
      ...nextAnswer,
    }
  } else if (!isEmptyAnswer) {
    result.answers = [
      ...result.answers,
      {
        id,
        question: question.id,
        image,
        untrimmedImage,
        text,
        ip_address,
        updated_at,
        created_at,
      },
    ]
  }

  return result
}

function applyOpenEndedMatrixAnswerToResponse(
  response,
  question,
  { rowData: rowId, columnData: columnId, value }
) {
  const result = {
    ...response,
  }

  const answerIndex = result.answers.findIndex(
    (a) => a.question_column === columnId && a.question_row === rowId
  )

  if (answerIndex >= 0) {
    result.answers[answerIndex].text_value = value
  } else {
    response.answers.push({
      id: null,
      question_column: columnId,
      question_row: rowId,
      text_value: value,
      question: question.id,
    })
  }

  return result
}

function applyOpenEndedTableAnswerToResponse(
  response,
  question,
  { rowData, columnData, value: { value, local, removeRow } }
) {
  const result = {
    ...response,
  }

  // remove answers related to row if both row and column are null
  if (rowData === null && columnData === null) {
    result.answers = flatMap(result.answers, (a) => {
      // check for existing answers
      const aColId = getMatrixCoordinateId(a.question_column)
      if (!aColId) return [a]
      const match = some(
        question.question_column,
        (c) => getMatrixCoordinateId(c) === aColId
      )
      if (!match) return [a]
      if (isNil(a?.id)) return []
      return [{ ...a, willRemove: true }]
    })
    return result
  }

  const answers = get(result, 'answers', [])

  if (removeRow === true) {
    if (local === true) {
      const filteredAnswers = reject(
        answers,
        (answer) =>
          getMatrixCoordinateId(
            answer.question_row ?? answer.answer_matrix_other_row
          ) === getMatrixCoordinateId(rowData)
      )
      return { ...result, answers: filteredAnswers }
    }
    const newAnswers = map(answers, (answer) => {
      if (
        getMatrixCoordinateId(
          answer.question_row ?? answer.answer_matrix_other_row
        ) !== getMatrixCoordinateId(rowData)
      ) {
        return answer
      }
      return {
        ...answer,
        text_value: '',
        willRemove: true,
      }
    })
    return { ...result, answers: newAnswers }
  }

  // Get current answer index
  const answerIndex = findIndex(
    answers,
    (answer) =>
      getMatrixCoordinateId(
        answer.question_row ?? answer.answer_matrix_other_row
      ) === getMatrixCoordinateId(rowData) &&
      getMatrixCoordinateId(answer.question_column) ===
        getMatrixCoordinateId(columnData)
  )

  if (answerIndex >= 0) {
    if (!hasText(value) && local === true) {
      return { ...result, answers: removeArrayIndex(answers, answerIndex) }
    }
    answers[answerIndex] = {
      ...answers[answerIndex],
      text_value: value,
      willRemove: !(answers[answerIndex]?.local === true || hasText(value)),
    }
    return { ...result, answers }
  }

  response.answers.push({
    id: null,
    question_column: getMatrixCoordinateId(columnData),
    question_row: getMatrixCoordinateId(rowData),
    text_value: value,
    willRemove: false,
    questionId: question.id, // deprecated, remove with care
    question: question.id,
    local,
  })
  return { ...result, answers }
}

function applyRadioMatrixAnswerToResponse(
  response,
  question,
  { rowData, columnData }
) {
  const result = {
    ...response,
  }

  // remove answers related to row if both row and column are null
  if (rowData === null && columnData === null) {
    result.answers = flatMap(result.answers, (a) => {
      // check for existing answers
      const aRowId = getMatrixCoordinateId(a.question_row)
      if (!aRowId) return [a]
      const match = some(
        question.question_rows,
        (r) => getMatrixCoordinateId(r) === aRowId
      )
      // no match, return [a]
      if (!match) return [a]
      // match, no id, return []
      if (isNil(a?.id)) return []
      return [{ ...a, willRemove: true }]
    })
    return result
  }

  const colIds = question.question_columns.map((c) => c.id)
  const rowId = getMatrixCoordinateId(rowData)

  // Remove all other answers for that row
  result.answers = reject(
    result.answers,
    (a) =>
      getMatrixCoordinateId(a.question_row) === rowId &&
      colIds.some((c) => c === getMatrixCoordinateId(a.question_column))
  )

  result.answers.push({
    id: null,
    question_column: columnData,
    question_row: rowData,
    question: question.id,
  })

  return result
}

function applyCheckboxMatrixAnswerToResponse(
  response,
  question,
  { rowData, columnData, value }
) {
  const result = {
    ...response,
  }

  // remove answers related to row if both row and column are null
  if (rowData === null && columnData === null) {
    result.answers = flatMap(result.answers, (a) => {
      // check for existing answers
      const aRowId = getMatrixCoordinateId(a.question_row)
      if (!aRowId) return [a]
      const match = some(
        question.question_rows,
        (r) => getMatrixCoordinateId(r) === aRowId
      )
      // no match, return [a]
      if (!match) return [a]
      // match, no id, return []
      if (isNil(a?.id)) return []
      return [{ ...a, willRemove: true }]
    })
    return result
  }

  const answers = get(result, 'answers', [])

  // Get current answer index
  const answerIndex = findIndex(
    answers,
    (answer) =>
      getMatrixCoordinateId(answer.question_row) ===
        getMatrixCoordinateId(rowData) &&
      getMatrixCoordinateId(answer.question_column) ===
        getMatrixCoordinateId(columnData)
  )

  if (answerIndex >= 0) {
    answers[answerIndex] = {
      ...answers[answerIndex],
      willRemove: answers[answerIndex]?.willRemove !== true,
    }
  } else {
    answers.push({
      id: null,
      question_column: columnData,
      question_row: rowData,
      willRemove: false,
      question: question.id,
    })
  }

  return { ...result, answers }
}

function answerApplicationFactory(question) {
  const { question_type } = question.question_type
  const { question_subtype } = question.question_type

  if (question_subtype === 'file_upload') {
    return applyFileAnswerToResponse
  }

  if (question_subtype === 'marker') {
    return applyMarkerAnswerToResponse
  }

  if (question_type === 'text' || question_subtype === 'handout') {
    return applySingleLineAnswerToResponse
  }

  if (question_type === 'number') {
    return applyNumberAnswerToResponse
  }

  if (question_type === 'checkbox') {
    return applyCheckboxListAnswerToResponse
  }

  if (question_type === 'radio') {
    return applyRadioListAnswerToResponse
  }

  if (question_type === 'datetime') {
    if (question_subtype === 'date_only' || question_subtype === 'month_year') {
      return applyDateOnlyAnswerToResponse
    }
    if (question_subtype === 'datetime') {
      return applyDateTimeAnswerToResponse
    }
    if (question_subtype === 'time_only') {
      return applyTimeOnlyAnswerToResponse
    }
  }

  if (question_type === 'signature') {
    return applySignatureAnswerToResponse
  }

  if (question_type === 'matrix') {
    if (question_subtype === 'open_ended') {
      return applyOpenEndedMatrixAnswerToResponse
    }
    if (question_subtype === 'radio') {
      return applyRadioMatrixAnswerToResponse
    }
    if (question_subtype === 'checkbox') {
      return applyCheckboxMatrixAnswerToResponse
    }
  }

  if (question_type === 'table') {
    if (question_subtype === 'open_ended') {
      return applyOpenEndedTableAnswerToResponse
    }
  }

  if (question_type === 'credit_card') {
    return applyCreditCardAnswerToResponse
  }

  return noOpApply
}

export function applyAnswerToResponse(response, question, answerContext) {
  return answerApplicationFactory(question)(response, question, answerContext)
}

export function isOptionalQuestionValid(response, question, setAnswerErrors) {
  // always valid questions
  const q_subtype = get(question, 'question_type.question_subtype')

  // base values
  const q_min_value = get(question, 'min_value')
  const q_max_value = get(question, 'max_value')
  const r_answers = get(response, 'answers', [])
  const q_type = get(question, 'question_type.question_type')
  const q_answer = find(r_answers, { question: question.id })

  let choices = []
  if (q_answer && q_answer?.answer_raw) {
    if (Array.isArray(q_answer.answer_raw)) {
      choices = q_answer.answer_raw
    } else if (typeof q_answer.answer_raw === 'string') {
      choices = q_answer.answer_raw.split(',')
    } else if (q_answer.answer_raw != null) {
      choices = q_answer.answer_raw
    }
  }

  const hasChoiceWithResponse = choices.length

  if (
    (['dropdown_multiple', 'vertical_list', 'horizontal_list'].includes(
      q_subtype
    ) ||
      (q_type !== 'radio' && q_subtype === 'dropdown_multiple')) &&
    hasChoiceWithResponse
  ) {
    const { disableMin, disableMax } = getDisabledFlagsByMinAndMax({
      min: q_min_value,
      max: q_max_value,
      total: choices?.length,
    })

    return !(disableMin || disableMax)
  }

  if (q_type === 'text') {
    if (q_subtype === 'phone') {
      return validatePhoneQuestion(
        question.id,
        q_answer?.answer_raw,
        setAnswerErrors
      )
    }
    if (q_subtype === 'email') {
      return validateEmailQuestion(
        question.id,
        q_answer?.answer_raw,
        setAnswerErrors
      )
    }
  }

  if (q_type === 'file_upload') {
    if (q_subtype === 'handout') {
      // required means the user must download the handout
      // readonly means the user can only view the handout
      if (question?.is_read_only) {
        return ['viewed', 'downloaded'].includes(q_answer?.answer_raw)
      }
      return true
    }
  }

  return true
}

export function isQuestionValid(
  response,
  question,
  setAnswerErrors,
  stripeEmpty
) {
  // always valid questions
  const q_subtype = get(question, 'question_type.question_subtype')
  const q_min_value = get(question, 'min_value')
  const q_max_value = get(question, 'max_value')
  if (q_subtype === 'markdown') return true
  if (q_subtype === 'image') return true
  if (q_subtype === 'scale') return true // TODO

  // base values
  const r_answers = get(response, 'answers', [])
  const q_type = get(question, 'question_type.question_type')
  const q_question_rows = get(question, 'question_rows', [])
  const q_answer = find(r_answers, { question: question.id })

  if (question?.hidden) return true
  let choices = []
  if (q_answer && q_answer?.answer_raw) {
    if (Array.isArray(q_answer.answer_raw)) {
      choices = q_answer.answer_raw
    } else if (typeof q_answer.answer_raw === 'string') {
      choices = q_answer.answer_raw.split(',')
    } else if (q_answer.answer_raw != null) {
      choices = q_answer.answer_raw
    }
  }

  const hasChoiceWithResponse = choices.length
  if (
    (['dropdown_multiple', 'vertical_list', 'horizontal_list'].includes(
      q_subtype
    ) &&
      hasChoiceWithResponse &&
      q_type === 'checkbox') ||
    (q_type === 'radio' &&
      q_subtype === 'dropdown_multiple' &&
      hasChoiceWithResponse)
  ) {
    const { disableMin, disableMax } = getDisabledFlagsByMinAndMax({
      min: q_min_value,
      max: q_max_value,
      total: choices?.length,
    })

    return !(disableMin || disableMax)
  }

  if (q_type === 'signature') {
    const hasDate = q_answer?.updated_at || q_answer?.created_at
    return !hasDate ? false : !!q_answer?.image || !!q_answer?.text
  }

  if (q_type === 'matrix') {
    if (q_subtype === 'radio') {
      return (
        every(q_question_rows, (row) =>
          some(r_answers, { question_row: { id: row.id } })
        ) ||
        every(q_question_rows, (row) =>
          some(r_answers, { question_row: row.id })
        )
      )
    }
    if (q_subtype === 'open_ended') {
      return every(q_question_rows, (row) =>
        some(
          r_answers,
          (answer) => answer.question_row === row.id && !!answer.text_value
        )
      )
    }
    return every(q_question_rows, (row) =>
      some(
        r_answers,
        (answer) =>
          answer.willRemove !== true && answer.question_row?.id === row.id
      )
    )
  }

  if (q_type === 'table') {
    return validateTableQuestion(r_answers, question)
  }

  if (q_type === 'file_upload') {
    if (q_subtype === 'file_upload') {
      const answer = find(get(response, 'answers', []), {
        question: question.id,
      })
      // has local file if answer?.isFile is valid
      // has remote file if answer?.file is valid
      return !!answer && (answer?.isFile || hasText(answer?.file))
    }
    if (q_subtype === 'handout') {
      // required means the user must download the handout
      // readonly means the user can only view the handout
      if (question?.is_required) {
        return q_answer?.answer_raw === 'downloaded'
      }
      if (question?.is_read_only) {
        return ['viewed', 'downloaded'].includes(q_answer?.answer_raw)
      }
      return true
    }
  }

  if (q_type === 'text') {
    if (q_subtype === 'phone') {
      return validatePhoneQuestion(
        question.id,
        q_answer?.answer_raw,
        setAnswerErrors,
        true
      )
    }
    if (q_subtype === 'email') {
      return validateEmailQuestion(
        question.id,
        q_answer?.answer_raw,
        setAnswerErrors,
        true
      )
    }
    if (q_subtype === 'full_name') {
      return validateNameQuestion(
        question.id,
        q_answer?.answer_raw,
        setAnswerErrors,
        true
      )
    }
  }

  if (q_subtype === 'stripe') {
    if (question?.is_required) {
      return !stripeEmpty
    }
    return true
  }

  // general question check
  return !!q_answer && !!q_answer.answer_raw
}

export function getQuestionError(answerErrors, question) {
  return answerErrors[question?.id ?? -1] ?? ''
}

export function removeErrorId(question, setAnswerError) {
  setAnswerError((answerErrors) => {
    if (isNil(answerErrors?.[question?.id])) return answerErrors
    const { [question.id]: removed, ...restErrors } = answerErrors
    return restErrors
  })
}

function validateQuestion(
  question,
  response,
  mode,
  onFailSetId,
  setAnswerErrors,
  stripeEmpty
) {
  const allQuestions = getAllQuestionsFromForm(response.form)

  if (
    !visibleByConditionalRulesHelper(question, response.answers, allQuestions)
  ) {
    return true
  }

  let validatorFunction = question.is_required
    ? isQuestionValid
    : isOptionalQuestionValid

  if (mode === 'office' && !question.is_office_use_only) {
    return true
  }

  if (mode === 'client' && question.is_office_use_only) {
    return true
  }

  const isValid = validatorFunction(
    response,
    question,
    setAnswerErrors,
    stripeEmpty
  )

  if (!isValid) {
    onFailSetId(question?.id)
  }

  return isValid
}

export function responseHasValidAnswers(
  response,
  mode,
  onFailSetId,
  setAnswerErrors,
  stripeEmpty
) {
  return response.form.pages.every((page) =>
    page.sections.every((section) => {
      return section.rows
        ? section.rows.every((row) =>
            row.questions.every((question) =>
              validateQuestion(
                question,
                response,
                mode,
                onFailSetId,
                setAnswerErrors,
                stripeEmpty
              )
            )
          )
        : section.questions.every((q) =>
            validateQuestion(
              q,
              response,
              mode,
              onFailSetId,
              setAnswerErrors,
              stripeEmpty
            )
          )
    })
  )
}
